import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { getUserInfo } from "@/api/user";
import { UserInfo } from "@/interfaces/authentication";
import { getCurrentApp, getVisibleApps } from "@/lib/appNavigation";
import ChevronDownIcon from "../icons/AikaIcons/ChevronDownIcon";

const AppSwitcher = () => {
  const { pathname } = useLocation();
  const { data: userInfo } = useQuery<UserInfo>({
    queryKey: ["userInfo"],
    queryFn: getUserInfo,
  });
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const visibleApps = getVisibleApps(userInfo);
  const currentApp = getCurrentApp(pathname);
  const showDropdown = visibleApps.length > 1;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (userInfo?.role === "TokenUser") {
    return null;
  }

  if (!showDropdown) {
    const app = visibleApps[0] ?? currentApp;
    return (
      <span className="app-switcher__label fw-semibold text-logo lh-sm">
        {app.label}
      </span>
    );
  }

  return (
    <div className="dropdown position-relative" ref={ref}>
      <button
        type="button"
        id="app-switcher-trigger"
        className="btn shadow-none app-switcher__trigger d-flex align-items-center gap-2 rounded-3"
        aria-expanded={open}
        aria-haspopup="true"
        aria-label="Vælg app"
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="text-truncate d-flex align-items-center gap-2 fw-semibold text-logo fs-4">
          {currentApp.label}
          <ChevronDownIcon height={16} />
        </span>
      </button>
      {open ? (
        <ul
          className="dropdown-menu show position-absolute d-flex flex-column gap-2 start-0 p-2 mt-1 shadow app-switcher__menu"
          style={{ minWidth: "16rem" }}
          role="menu"
        >
          {visibleApps.map((app) => {
            const isActive = app.id === currentApp.id;
            return (
              <li role="none" key={app.id}>
                <Link
                  id={`app-switcher-${app.id}`}
                  className={`dropdown-item rounded-3${isActive ? " active fw-semibold" : ""}`}
                  to={app.basePath}
                  role="menuitem"
                  onClick={() => setOpen(false)}
                >
                  <span className="d-block">{app.label}</span>
                  {app.description ? (
                    <small
                      className={`${isActive ? "text-white opacity-50 fw-medium  " : "text-muted opacity-75"}`}
                    >
                      {app.description}
                    </small>
                  ) : null}
                </Link>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

export default AppSwitcher;
